import { keyframes, styled } from '@styledComponents';
import { Box } from '@components/Box';
import { CloudImage, PlaceholderCloudImage } from '@components/CloudImage';
import { variant } from 'styled-system';
import { themeColor, themeSpace, themeVariant } from '@utils/styledTheme.util';
import { Column, Columns } from '@components/Columns';
import { animationTransition } from '@utils/animationTransition';
import { CTALink } from '@components/CTALink';
import { Text } from '@components/typography';
import { PhotoCredit } from '@components/PhotoCredit';
import { themeMediaQuery } from '@utils/styledTheme.util';

export const topBarHeight = '139px';

export const StyledSection = styled(Box)`
  height: 100vh;
  position: relative;
  z-index: 1;
  contain: strict;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const topBorderHeight = '32px';

export const ContentWrapper = styled.div`
  border-top: solid 0px ${themeColor('mono6')};
  height: 100%;
  width: 100%;
  display: flex;
  background-color: ${themeColor('mono6')};
  z-index: 0;
`;

export const MaskedCloudImage = styled(CloudImage)<{ maskId: string }>`
  clip-path: url(#${({ maskId }) => maskId});
`;

export const StyledColumns = styled(Columns)`
  height: 100%;
  position: relative;
  z-index: 2;
  ${themeMediaQuery('sm')} {
    min-width: 57rem;
  }
`;

export const FeaturesColumn = styled(Column)`
  height: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  background-color: ${themeColor('mono6')};
`;

export const rightBlockWidthRatio = 7 / 12;

export const FeatureContentColumn = styled(Column)`
  height: 100%;
`;

const FeatureBaseBFImage = styled(PlaceholderCloudImage)<{ aspectratio: number; scale?: number }>`
  width: 100%;
  min-width: calc(100vh * ${({ aspectratio }) => aspectratio});
  min-height: 100%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) ${({ scale }) => (scale ? `scale(${scale})` : '')};
  transform-origin: center center;
  will-change: transform;
  object-fit: cover;
`;

export const FeatureBackgroundImage = styled(FeatureBaseBFImage).attrs({ className: 'home-product-feature-background' })<{
  aspectratio: number;
  isEagerLoading: boolean;
}>``;

export const RegistryFeatureBackgroundImage = styled(FeatureBaseBFImage).attrs({ className: 'home-product-feature-background' })<{
  aspectratio: number;
  isEagerLoading: boolean;
}>`
  top: 47.5%;
  min-width: calc((107vh - ${topBorderHeight}) * ${({ aspectratio }) => aspectratio});
`;

export const FeatureUiImage = styled(PlaceholderCloudImage).attrs({ className: 'home-product-feature-ui' })<{
  aspectratio: number;
  scale?: number;
}>`
  width: 90%;
  min-width: calc((100vh - ${topBorderHeight}) * ${({ aspectratio }) => aspectratio} * 0.5);
  min-height: calc(100vw * (${rightBlockWidthRatio}) / ${({ aspectratio }) => aspectratio} * 0.5);
  position: absolute;
  left: 52%;
  opacity: 0;
  top: calc(100px + 8%);
  transform: translate(-50%, 0) ${({ scale }) => (scale ? `scale(${scale})` : '')};
  transform-origin: center center;
`;

export const RegistryFeatureUiImage = styled(FeatureUiImage)`
  transform: translate(-53%, 35%) ${({ scale }) => (scale ? `scale(${scale})` : '')};
`;

export const InvitationsFeatureUiImageContainer = styled('div')<{ aspectratio: number }>`
  width: 100%;
  height: 100%;
  min-width: calc((100vh - ${topBorderHeight}) * ${({ aspectratio }) => aspectratio});
  min-height: calc(100vw * (${rightBlockWidthRatio}) / ${({ aspectratio }) => aspectratio});
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transform-origin: center center;
  will-change: transform;
`;

export const FeatureForegroundImage = styled(FeatureBaseBFImage).attrs({ className: 'home-product-feature-foreground' })<{
  maskId?: string;
}>`
  clip-path: ${({ maskId }) => (maskId ? `url(#${maskId})` : 'none')};
  transform-origin: center bottom;
`;

export const RegistryFeatureForegroundImage = styled(FeatureForegroundImage)`
  min-width: calc((60vh - ${topBorderHeight}) * ${({ aspectratio }) => aspectratio});
  transform: translate(-50%, -50%) ${({ scale }) => (scale ? `scale(${scale})` : '')};
`;

export const FeaturesList = styled.div<{ isControlVersion: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${themeColor('mono6')};
  padding-top: 0;
  ${themeMediaQuery('lg')} {
    padding-top: 48px;
  }

  > *:not(:first-child) {
    padding-top: ${themeSpace(3)};
    ${themeMediaQuery('sm')} {
      padding-top: 0.25rem;
    }
    ${themeMediaQuery('lg')} {
      padding-top: ${themeSpace(6)};
    }
  }
`;

export const FeatureAnimationWrapper = styled.div<{ isFirst?: boolean; isForHover?: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  will-change: transform;
  contain: strict;
  opacity: ${({ isForHover }) => (isForHover ? 0 : 1)};
  > div {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
    transform: ${({ isFirst, isForHover }) => (isFirst || isForHover ? 'translate(0px, 0px)' : 'translate(0px, 100%)')};
  }
`;

const fadeInAnimation = keyframes`
  0%{opacity: 0.3}
  0%{opacity: 0.6}
  100%{opacity: 1}
`;

export const FeatureItem = styled(CTALink).attrs({ variant: 'homeFeature' })<{
  $isActive?: boolean;
  $isHidden?: boolean;
}>`
  color: ${({ $isActive }) => ($isActive ? themeColor('linkHomeFeatureHover') : themeColor('mono1'))};
  transition: ${animationTransition('color', 'transform')};
  ${themeVariant('navChild32')}
  ${themeMediaQuery('lg')} {
    ${themeVariant('hed40')}
  }
  font-style: normal;
  svg {
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
    transition: ${animationTransition('opacity', 'transform')};
  }
  &:hover {
    svg {
      opacity: 1;
      transform: translate(20%, 0);
    }
  }
`;

const fadeInLinkDesc = keyframes`
  0% { opacity: 0 }
  100% { opacity: 1 }
`;

export const LinkDescription = styled(Text)`
  opacity: 1;
  animation: ${fadeInLinkDesc} 0.5s ease-out;
  max-width: 317px;
  margin-top: 0;
  white-space: normal;
  font-style: normal;
  ${themeMediaQuery('lg')} {
    margin-top: ${themeSpace(5)};
  }
`;

export const LinkTitle = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  white-space: break-spaces;
  padding: ${themeSpace(0)};
`;

export const ChevronWrapper = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  transform: translateY(1px);
  > div {
    width: 24px;
    height: 24px;
  }
  svg {
    width: 24px;
    height: 24px;
  }
  ${themeMediaQuery('lg')} {
    transform: translateY(2px);
    > div {
      width: 32px;
      height: 32px;
    }
    svg {
      width: 32px;
      height: 32px;
    }
  }
`;

export const FeatureItemWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

export const AssetLink = styled(CTALink)`
  width: 100%;
  height: 100%;
  position: absolute;
`;

export const PhotoCreditContent = styled(PhotoCredit)`
  bottom: -6px !important;
  animation: ${fadeInAnimation} 3s;
`;
